import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'router';
import { useDispatch, useSelector } from 'react-redux';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import {
  getOrganisationCreationCode,
  getPartnerProduct,
} from 'store/slices/registrationSlice';
import { PartnerRegister as PartnerRegisterPage } from '@karehero/llama/pwa/pages/PartnerRegister/PartnerRegister';
import { PartnerDetails } from '@karehero/models';
import { useCreateReferringOrganizationMutation } from 'store/api/organization';
import { getAccount, invalidateAccount } from 'store/slices/iamSlice';
import { newToast } from 'store/slices/uiSlice';
import keni from '@karehero/llama/assets/loader/keni.svg';

const validatePhone = (phone?: string): boolean => {
  if (!phone) return false;
  const digitsOnly = phone.replace(/\D/g, '');
  return digitsOnly.length >= 10;
};

const validatePartnerCode = (code?: string): boolean => {
  if (!code) return false;
  return /^[0-9]{6}[A-Z]?$/i.test(code.trim());
};

export const PartnerRegister = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const organisationCreationCode =
    useSelector(getOrganisationCreationCode) || undefined;
  const partnerProduct = useSelector(getPartnerProduct);
  const account = useSelector(getAccount);
  const { user: kindeUser } = useKindeAuth();
  const firstName = account?.firstName || kindeUser?.givenName;
  const lastName = account?.lastName || kindeUser?.familyName;
  const [createReferringOrganization] =
    useCreateReferringOrganizationMutation();

  const requiresPartnerCode = partnerProduct?.requiresPartnerCode ?? true;
  const requiresLicenceCode = partnerProduct?.requiresLicenceCode ?? true;

  const handleSubmit = useCallback(
    async (partner: Partial<PartnerDetails>) => {
      const resolvedFirstName = partner.firstName || firstName;
      const resolvedLastName = partner.lastName || lastName;
      if (!resolvedFirstName || !resolvedLastName) {
        dispatch(
          newToast({
            title: 'Error',
            description: 'Please provide your first and last name.',
            variant: 'error',
          }),
        );
        return;
      }

      try {
        const result = await createReferringOrganization({
          firstName: resolvedFirstName,
          lastName: resolvedLastName,
          partnerCode: partner.partnerCode,
          referLtcCode: partner.ltcLicense,
          phoneNumber: partner.phoneNumber,
          organisationCreationCode,
        });

        if ('error' in result) {
          console.error(
            '[PartnerRegister] Create referring organization error',
            result.error,
          );
          dispatch(
            newToast({
              title: 'Error',
              description:
                'Failed to register. Please check your information and try again.',
              variant: 'error',
            }),
          );
          return;
        }

        if ('data' in result) {
          dispatch(invalidateAccount());

          dispatch(
            newToast({
              title: 'Success',
              description: 'Partner registration completed successfully.',
              variant: 'success',
            }),
          );

          navigate(ROUTES.PORTAL);
        }
      } catch (error) {
        console.error('[PartnerRegister] Unexpected error', error);
        dispatch(
          newToast({
            title: 'Error',
            description: 'An unexpected error occurred. Please try again.',
            variant: 'error',
          }),
        );
      }
    },
    [
      createReferringOrganization,
      dispatch,
      navigate,
      organisationCreationCode,
      firstName,
      lastName,
    ],
  );

  return (
    <PartnerRegisterPage
      topImage={keni}
      onSubmit={handleSubmit}
      validatePhone={validatePhone}
      validatePartnerCode={validatePartnerCode}
      requiresPartnerCode={requiresPartnerCode}
      requiresLicenceCode={requiresLicenceCode}
      firstName={firstName}
      lastName={lastName}
    />
  );
};

PartnerRegister.displayName = 'PartnerRegister';
export default PartnerRegister;
